import React, { useEffect, useState } from "react";
import { ShieldCheck, AlertTriangle, Building2, Mail, Phone, Globe, MapPin, Hash, ChevronDown } from "lucide-react";
import { useLang } from "../i18n";
import { getGpsrForBrand } from "../lib/api";

/**
 * GPSR (Reg. UE 2023/988) product safety section.
 * Shows manufacturer + EU responsible person + safety warnings
 * based on the product's brand. Renders a visible placeholder
 * if the brand has no verified data yet.
 *
 * Produttore and Responsabile UE are presented as collapsible
 * accordions (closed by default) for a tidier scheda prodotto.
 */
const Row = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-sm">
      <Icon className="w-4 h-4 mt-0.5 shrink-0 text-slate-500" strokeWidth={2.2} />
      <div className="min-w-0">
        <span className="font-bold text-slate-900">{label}:</span>{" "}
        <span className="text-slate-700 break-words">{value}</span>
      </div>
    </div>
  );
};

const PartyAccordion = ({ title, data, t, sameAs = false, testId }) => {
  const [open, setOpen] = useState(false);
  if (!data) return null;
  return (
    <div className="bg-white border-2 border-slate-900 rounded-2xl overflow-hidden">
      <button
        type="button"
        data-testid={testId}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 p-3 hover:bg-amber-50 transition-colors text-left"
      >
        <Building2 className="w-4 h-4 text-pink-600 shrink-0" strokeWidth={2.5} />
        <span className="font-display font-bold text-base flex-1">
          {title}
        </span>
        {sameAs && (
          <span className="text-xs italic text-slate-500 hidden sm:inline">
            {t("gpsr.same_as_manufacturer")}
          </span>
        )}
        <ChevronDown
          className={`w-5 h-5 text-slate-600 transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={2.5}
        />
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-2 border-t-2 border-slate-900/10">
          {sameAs && (
            <p className="text-xs italic text-slate-500 sm:hidden">
              {t("gpsr.same_as_manufacturer")}
            </p>
          )}
          <Row icon={Building2} label={t("gpsr.brand")} value={data.legal_name} />
          <Row icon={MapPin} label={t("gpsr.address")} value={data.address} />
          <Row icon={Hash} label={t("gpsr.vat")} value={data.vat} />
          <Row icon={Mail} label={t("gpsr.email")} value={data.email} />
          <Row icon={Phone} label={t("gpsr.phone")} value={data.phone} />
          {data.website && (
            <div className="flex items-start gap-2 text-sm">
              <Globe className="w-4 h-4 mt-0.5 shrink-0 text-slate-500" strokeWidth={2.2} />
              <div className="min-w-0">
                <span className="font-bold text-slate-900">{t("gpsr.website")}:</span>{" "}
                <a
                  href={data.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sky-700 underline break-words"
                >
                  {data.website}
                </a>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const GpsrInfo = ({ brand }) => {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!brand?.slug) {
      setData(null);
      return;
    }
    setLoading(true);
    getGpsrForBrand(brand.slug, brand.name || "")
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [brand?.slug, brand?.name]);

  if (!brand?.slug) return null;
  if (loading || !data) return null;

  const eu = data.eu_responsible_person || {};
  const isPlaceholder = !!data.is_placeholder;

  return (
    <section
      data-testid="gpsr-section"
      className="mt-12 bg-amber-100 border-4 border-slate-900 rounded-3xl p-6 cartoon-shadow"
    >
      <header className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 bg-emerald-300 border-2 border-slate-900 rounded-full flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-slate-900" strokeWidth={2.5} />
        </div>
        <div>
          <h3 className="font-display font-bold text-xl sm:text-2xl text-slate-900">
            {t("gpsr.section_title")}
          </h3>
          <p className="text-xs font-bold text-slate-600 tracking-wider uppercase">
            {t("gpsr.section_subtitle")}
            {" · "}
            <span className="text-pink-700">{data.brand_name}</span>
          </p>
        </div>
      </header>

      {isPlaceholder && (
        <div className="mt-3 mb-4 bg-yellow-200 border-2 border-amber-700 rounded-xl p-3 flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-800 shrink-0 mt-0.5" strokeWidth={2.5} />
          <p className="text-sm text-amber-900 font-medium">
            {t("gpsr.placeholder_notice")}
          </p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3 mt-4">
        <PartyAccordion
          title={t("gpsr.manufacturer")}
          data={data.manufacturer}
          t={t}
          testId="gpsr-toggle-manufacturer"
        />
        <PartyAccordion
          title={t("gpsr.eu_responsible")}
          data={eu}
          t={t}
          sameAs={!!eu.same_as_manufacturer}
          testId="gpsr-toggle-eu-responsible"
        />
      </div>

      {Array.isArray(data.safety_warnings) && data.safety_warnings.length > 0 && (
        <div className="mt-4 bg-white border-2 border-slate-900 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-600" strokeWidth={2.6} />
            <h4 className="font-display font-bold text-base">
              {t("gpsr.warnings")}
            </h4>
          </div>
          <ul className="space-y-1.5 text-sm text-slate-800 list-disc list-inside leading-relaxed">
            {data.safety_warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export default GpsrInfo;
